import { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { FormGroup, Input, Label } from "reactstrap";

//Common
import FormBackGround from "../common/Forms/FormBackGround/FormBackGround";
import FormHeader from "../common/Forms/FormHeader/FormHeader";
import ActionBar from "../common/ActionBar/ActionBar";
import { EMPTY_GUID } from "../../utility/constants/constants";
//App
import {
  TextField,
  Autocomplete,
  MaskedZipCode,
  MaskedPhoneNumberField,
  RadioGroup,
  Chip,
  Button,
  ConfirmationDialog,
} from "../common/VFX-UI";
import { OrganizationFormProps } from "../../types/organization";
import { toOptions } from "../../utility/helpers/lookupHelper";
import useOrganizationStyles from "./styles";
import { ActivateIcon, BlockIcon } from "../common/Icons";
import useComponentGeneralStyles from "../styles";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .max(250, "Max 250 characters are allowed."),
  addressLine1: Yup.string().required("Address Line1 is required"),
  city: Yup.string().required("City is required"),
  zipCode: Yup.string().required("Zip Code is required"),
  mainPhone: Yup.string().required("Main Phone is required"),
  stateId: Yup.array()
    .min(1, "State is required")
    .required("State is required"),
  websiteUrl: Yup.string().nullable().notRequired().url("Enter a valid URL"),
  contactEmail: Yup.string()
    .nullable()
    .notRequired()
    .email("Enter a valid email"),
});

const OrganizationForm = (props: OrganizationFormProps) => {
  const { lookups, status, initialVal, onClose, handleOnSubmit } = props;
  const formRef = useRef<FormikProps<any>>(null);
  const organizationStyles = useOrganizationStyles();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [base64String, setBase64String] = useState<string | null>(null);
  const [isActiveDialog, setIsActiveDialog] = useState<boolean>(false);
  const [isInActiveDialog, setIsInActiveDialog] = useState<boolean>(false);
  const { state, payrollDeductionFrequency } = lookups ?? {};
  const payrollFrequencyOptions = toOptions(payrollDeductionFrequency ?? []);
  const stateOptions = toOptions(state ?? []);
  const generalStyles = useComponentGeneralStyles();

  useEffect(() => {
    if (initialVal?.logo) {
      setBase64String(
        `data:${initialVal.logo.mimeType};base64,${initialVal.logo.content}`
      );
    }
  }, []);

  const saveInput = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any
  ) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".png")) {
      setErrorMessage("Please upload logo with .png extension.");
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64Data = reader.result as string;
      setBase64String(base64Data);
      const splittedData = base64Data?.split(";");
      const mimeType = splittedData && splittedData[0].split(":");
      const content = splittedData && splittedData[1].split(",");

      const logo = {
        id: EMPTY_GUID,
        fileName: file.name,
        content: content && content[1],
        mimeType: mimeType && mimeType[1],
      };

      setFieldValue("logo", logo);
    };
    reader.readAsDataURL(file);
  };

  return (
    <FormBackGround>
      <div>
        <FormHeader
          title={
            initialVal.id !== EMPTY_GUID
              ? "Edit Organization"
              : "New Organization"
          }
          onClose={onClose}
        />
        <Formik
          onSubmit={handleOnSubmit}
          initialValues={initialVal}
          validationSchema={validationSchema}
          innerRef={formRef}
        >
          {({ values, setFieldValue }) => (
            <Form noValidate>
              <div className="d-flex justify-content-end mb-3 me-3">
                <Chip label={values.isActive ? "Active" : "Inactive"} />
              </div>
              <TextField
                isRequired={true}
                label="Name"
                type="text"
                name="name"
              />
              <TextField
                isRequired={false}
                label="Description"
                type="textarea"
                name="description"
              />
              <TextField
                isRequired={true}
                label="Address Line1"
                type="text"
                name="addressLine1"
              />
              <TextField
                isRequired={false}
                label="Address Line2"
                type="text"
                name="addressLine2"
              />
              <TextField
                isRequired={true}
                label="City"
                type="text"
                name="city"
              />
              {stateOptions?.length > 0 && (
                <Autocomplete
                  name="stateId"
                  label="State"
                  isRequired={true}
                  options={stateOptions ?? []}
                  defaultSelected={
                    initialVal.stateId != null && initialVal.stateId.length > 0
                      ? [initialVal.stateId[0]]
                      : []
                  }
                />
              )}
              <MaskedZipCode
                name="zipCode"
                label="Zip Code"
                isRequired={true}
              />
              <MaskedPhoneNumberField
                name="mainPhone"
                label="Main Phone"
                isRequired={true}
              />
              <MaskedPhoneNumberField
                name="fax"
                label="Fax"
                isRequired={false}
              />
              <TextField label="Website Url" type="text" name="websiteUrl" />
              <RadioGroup
                label="Payroll Frequency"
                options={payrollFrequencyOptions ?? []}
                name="payrollDeductionFrequencyId"
                onChange={() => {}}
              />
              <FormGroup>
                <Label for="logo">
                  Logo{" "}
                  <span style={{ fontSize: 12 }}>
                    {" "}
                    *recomended height of 36px
                  </span>
                </Label>
                <Input
                  id="logo"
                  type="file"
                  name="logo"
                  accept=".png"
                  onChange={(e) => {
                    handleFileUpload(e, setFieldValue);
                  }}
                  invalid={!!errorMessage}
                />
                {errorMessage && (
                  <p className="invalid-feedback">{errorMessage}</p>
                )}
                {base64String && (
                  <div className="mt-4">
                    <h6>Preview:</h6>
                    <img
                      src={base64String as string}
                      alt="Uploaded file"
                      style={organizationStyles.logoPreview}
                    />
                  </div>
                )}
              </FormGroup>
              <Label for="contactInformation">Contact Information</Label>
              <TextField label="Contact Name" type="text" name="contactName" />
              <MaskedPhoneNumberField name="contactPhone" label="Phone" />
              <TextField
                label="Contact Email"
                type="text"
                name="contactEmail"
              />

              <div className="border-top container-fluid p-3 mt-auto">
                {values.isActive && (
                  <Button
                    style={generalStyles.deleteButton}
                    onClick={() => setIsInActiveDialog(true)}
                  >
                    <BlockIcon />
                  </Button>
                )}
                {!values.isActive && (
                  <Button
                    style={generalStyles.activateButton}
                    onClick={() => setIsActiveDialog(true)}
                  >
                    <ActivateIcon />
                  </Button>
                )}
              </div>
              <ActionBar
                submitHandler={saveInput}
                cancelHandler={onClose}
                submitText="Submit"
                cancelText="Cancel"
                isLoading={status === "pending"}
              ></ActionBar>
              <ConfirmationDialog
                open={isActiveDialog}
                onClose={() => setIsActiveDialog(false)}
                onConfirm={() => {
                  setIsActiveDialog(false);
                  setFieldValue("isActive", true);
                }}
                contentTitle={`Activate Organization`}
                contentText={
                  "Are you sure you want to continue? This will only change active state. You will still need to hit Submit to save changes."
                }
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
              />
              <ConfirmationDialog
                open={isInActiveDialog}
                onClose={() => setIsInActiveDialog(false)}
                onConfirm={() => {
                  setIsInActiveDialog(false);
                  setFieldValue("isActive", false);
                }}
                contentTitle={`Deactivate Organization`}
                contentText={
                  "Are you sure you want to continue?  This will only change active state. You will still need to hit Submit to save changes."
                }
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
              />
            </Form>
          )}
        </Formik>
      </div>
    </FormBackGround>
  );
};

export default OrganizationForm;
